import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { sort } from 'fast-sort';
import {
  EstateInputDataFormatCategory,
  EstateInputDataFormatDataType,
  EstateInputDataInstance_FieldsFragment,
  UpdateEstateInputDataInstanceInput,
} from 'queries';
import * as yup from 'yup';

import FormDateField from 'common/form-date-field';
import FormPhoneField from 'common/form-phone-field';
import FormSelectField from 'common/form-select-field';
import FormSsnField from 'common/form-ssn-field';
import FormTextField from 'common/form-text-field';

const useStyles = makeStyles((theme) => ({
  container: {
    width: 800,
  },
  title: {
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '15px',
    textTransform: 'capitalize',
  },
  titleSecondary: {
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: 700,
    marginBottom: '15px',
    textTransform: 'capitalize',
  },
  questions: {
    width: '100%',
  },
  contentContainer: {
    padding: theme.spacing(3),
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  subcategoryContainer: {
    marginBottom: theme.spacing(3),
  },
  footerContainer: {
    height: 76,
    padding: '0 80px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'flex-end',
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  footerButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
  },
  actionButton: {
    borderRadius: '100px',
  },
  saveAndExit: {
    backgroundColor: '#fff',
    borderRadius: '100px',
    color: '#1C2541',
    border: '1px solid #1C2541',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F0F3F6',
    },
  },
  backButton: {
    color: '#1C2541',
    cursor: 'pointer',
    borderBottom: '1px solid #1C2541',
    fontSize: 16,
    '&:hover': {
      color: '#1c96c5',
    },
  },
}));

const schema = yup.object().shape({
  updateEstateInputDataInstanceInputs: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().required(),
        data: yup.string().nullable(),
        dataType: yup.string<EstateInputDataFormatDataType>().nullable().notRequired(),
        portalId: yup.string().required(),
      }),
    )
    .required(),
});

type EstateInputDataInstancesFormProps = {
  estateInputDataInstances: EstateInputDataInstance_FieldsFragment[];
  category: EstateInputDataFormatCategory;
  onSubmit: (data: UpdateEstateInputDataInstanceInput[]) => void;
  portalId: string;
  handleBackClick?: () => void;
  handleSaveAndContinueClick: () => void;
  onExit: () => void;
  isLastCategory: boolean;
  onGenerateDocument: (data: {
    updateEstateInputDataInstanceInputs: UpdateEstateInputDataInstanceInput[];
  }) => void;
};

export type EstateInputDataInstanceFormInputValues = UpdateEstateInputDataInstanceInput & {
  dataType?: EstateInputDataFormatDataType | null;
};

type FormInputValues = {
  updateEstateInputDataInstanceInputs: EstateInputDataInstanceFormInputValues[];
};

const EstateInputDataInstancesForm = ({
  estateInputDataInstances,
  category,
  onSubmit,
  portalId,
  handleBackClick,
  handleSaveAndContinueClick,
  isLastCategory,
  onExit,
  onGenerateDocument,
}: EstateInputDataInstancesFormProps) => {
  const classes = useStyles();
  const formDefaultValues = sort(estateInputDataInstances)
    .asc((estateInputDataInstance) => estateInputDataInstance.estateInputDataFormat.name)
    .map((instance) => ({
      id: instance.id!,
      data: instance.data,
      dataType: instance.estateInputDataFormat.dataType,
      portalId,
    }));

  const estateInputDataInstancesDict = Object.fromEntries(
    estateInputDataInstances.map((instance) => [instance.id, instance]),
  );

  const subcategories = sort(
    Array.from(
      new Set(
        estateInputDataInstances.map((instance) => instance.estateInputDataFormat.subcategory),
      ),
    ),
  ).asc((subcategory) => subcategory || '\uFFFF'); // Using a high Unicode value to place empty strings last

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<FormInputValues>({
    resolver: yupResolver(schema),
    values: { updateEstateInputDataInstanceInputs: formDefaultValues },
  });

  const { fields } = useFieldArray({
    control,
    name: 'updateEstateInputDataInstanceInputs',
    keyName: 'fieldId',
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  const handleFormSubmit = (data: FormInputValues) => {
    onSubmit(data.updateEstateInputDataInstanceInputs);
  };

  const handleSubmitForm = () => {
    if (isLastCategory) {
      return handleSubmit(onGenerateDocument)();
    }
    handleSaveAndContinueClick();
  };

  useEffect(() => {
    // This is to handle the case where the user navigates away from the page without saving
    // The navigation wizard is handled by the parent component, so when the user navigates away, this callback is called to save the current state of the form
    return () => {
      handleSubmit(handleFormSubmit)();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderEstateInputDataInstanceField = (
    estateInputDataInstance: EstateInputDataInstance_FieldsFragment,
    index: number,
  ) => {
    const label = estateInputDataInstance.estateInputDataFormat.name;
    const isTextSingleLine =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.TextSingleLine;

    const isTextMultiLine =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.TextMultiLine;

    const isNumber =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.Number;

    const isNumberNoDecimal =
      estateInputDataInstance.estateInputDataFormat.dataType ===
      EstateInputDataFormatDataType.NumberNoDecimal;

    switch (estateInputDataInstance.estateInputDataFormat.dataType) {
      case EstateInputDataFormatDataType.TextSingleLine:
      case EstateInputDataFormatDataType.TextMultiLine:
      case EstateInputDataFormatDataType.Number:
      case EstateInputDataFormatDataType.NumberNoDecimal:
      case EstateInputDataFormatDataType.List: // TODO - render list type once list infrastructure is implemented
      default:
        return (
          <Controller
            name={`updateEstateInputDataInstanceInputs.${index}.data`}
            control={control}
            render={({ field: { ref: _, onChange, ...field } }) => (
              <FormTextField
                {...field}
                label={label}
                onChange={(evt) => {
                  const value = evt.target.value;
                  if (isNumber || isNumberNoDecimal) {
                    if (value && !isNaN(Number(value))) {
                      let numberValue = Number(value);

                      if (isNumberNoDecimal) {
                        numberValue = Math.trunc(numberValue);
                      }

                      onChange(numberValue.toString());
                    } else {
                      onChange(null);
                    }
                  } else {
                    onChange(value);
                  }
                }}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                inputProps={{
                  maxLength: isTextSingleLine || isTextMultiLine ? 80 : undefined,
                  type: isNumber || isNumberNoDecimal ? 'number' : undefined,
                  pattern: isNumberNoDecimal ? '[0-9]*' : undefined,
                }}
                multiline={isTextMultiLine}
              />
            )}
          />
        );

      case EstateInputDataFormatDataType.Date:
        return (
          <Controller
            name={`updateEstateInputDataInstanceInputs.${index}.data`}
            control={control}
            render={({ field: { value, onChange, name } }) => (
              <FormDateField
                value={new Date(value || '')}
                onChange={(date) => {
                  onChange(date ? date.toISOString() : '');
                }}
                label={label}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                name={name}
              />
            )}
          />
        );

      case EstateInputDataFormatDataType.YesNo:
        return (
          <Controller
            name={`updateEstateInputDataInstanceInputs.${index}.data`}
            control={control}
            render={({ field: { ref: _, onChange, ...field } }) => (
              <FormSelectField
                {...field}
                onChange={(evt) => {
                  onChange(evt.target.value);
                }}
                label={label}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                options={[
                  {
                    value: '',
                    label: '',
                  },
                  {
                    value: 'true',
                    label: 'Yes',
                  },
                  {
                    value: 'false',
                    label: 'No',
                  },
                ]}
              />
            )}
          />
        );
      case EstateInputDataFormatDataType.Ssn:
        return (
          <Controller
            name={`updateEstateInputDataInstanceInputs.${index}.data`}
            control={control}
            render={({ field: { ref, ...field } }) => (
              <FormSsnField
                {...field}
                label={label}
                inputRef={ref}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
              />
            )}
          />
        );

      case EstateInputDataFormatDataType.PhoneNumber:
        return (
          <Controller
            name={`updateEstateInputDataInstanceInputs.${index}.data`}
            control={control}
            render={({ field: { name, onBlur, onChange, value } }) => (
              <FormPhoneField
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                label={label}
                helperText={estateInputDataInstance.estateInputDataFormat.labelHint}
                errorMessage={errors.updateEstateInputDataInstanceInputs?.[index]?.message}
              />
            )}
          />
        );
    }
  };

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      <div className={classes.contentContainer}>
        <div className={classes.container}>
          <Typography className={classes.title}>
            {category.replace('_', ' ').toLowerCase()}
          </Typography>

          {subcategories.map((subcategory) => (
            <div key={subcategory} className={classes.subcategoryContainer}>
              <Typography className={classes.titleSecondary}>{subcategory || 'Other'}</Typography>
              <Grid container rowSpacing={1} columnSpacing={4}>
                {fields
                  .map((estateInputDataInstanceField, originalIndex) => ({
                    ...estateInputDataInstanceField,
                    originalIndex,
                  }))
                  .filter(
                    (estateInputDataInstanceField) =>
                      estateInputDataInstancesDict[estateInputDataInstanceField.id]
                        .estateInputDataFormat.subcategory === subcategory,
                  )
                  .map((estateInputDataInstanceField) => (
                    <Grid item xs={12} md={6} key={estateInputDataInstanceField.id}>
                      {renderEstateInputDataInstanceField(
                        estateInputDataInstancesDict[estateInputDataInstanceField.id],
                        estateInputDataInstanceField.originalIndex,
                      )}
                    </Grid>
                  ))}
              </Grid>
            </div>
          ))}
        </div>
      </div>
      <div
        className={clsx(classes.footerContainer, {
          [classes.spaceBetween]: Boolean(handleBackClick),
        })}
      >
        {handleBackClick && (
          <Typography
            className={classes.backButton}
            onClick={() => {
              handleBackClick();
            }}
          >
            Back
          </Typography>
        )}

        <div className={classes.footerButtonsContainer}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.saveAndExit}
            type="button"
            onClick={onExit}
          >
            Save and Exit
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.actionButton}
            type="submit"
          >
            {isLastCategory ? 'Generate Document' : 'Save and Continue'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EstateInputDataInstancesForm;
